import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Button,
  HStack,
  VStack,
  Badge,
  Text,
  Select,
  Input,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
  useColorModeValue,
  Flex,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Tooltip,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  SimpleGrid,
} from '@chakra-ui/react';
import { 
  FiRefreshCw, 
  FiFilter, 
  FiTrash2, 
  FiDownload, 
  FiAlertTriangle,
  FiActivity,
  FiWifi,
  FiBattery,
  FiClock,
  FiTrendingUp,
  FiBarChart2,
  FiPieChart,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ComposedChart,
} from 'recharts';
import { format, subDays, subHours, startOfDay, endOfDay } from 'date-fns';
import DataTable, { Column } from '@/components/common/DataTable';
import apiClient from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

// Types based on the API specification
interface DeviceLog {
  logged_at: number; // Unix timestamp in milliseconds
  device_id: string;
  status: string;
  last_seen: number; // Unix timestamp in milliseconds
  busy?: boolean;
  blocked?: boolean;
  sensor?: boolean;
  battery?: number;
  alarm_status?: number;
  alarm_description?: string;
  ble_signal?: number;
  gsm_signal?: number;
  source?: 'MQTT' | 'API' | 'BLE' | 'MANUAL';
  uid?: string;
}

interface DeviceAnalytics {
  device_id: string;
  total_logs: number;
  status_distribution?: Record<string, number>;
  alarm_count?: number;
  blocked_count?: number;
  time_range: {
    start?: string | number;
    end?: string | number;
  };
  last_activity?: string | number;
  [key: string]: any;
}

interface DeviceLogFilters {
  device_id?: string;
  device_ids?: string[];
  status?: string | string[];
  blocked?: boolean;
  sensor?: boolean;
  has_alarm?: boolean;
  start_time?: string | number;
  end_time?: string | number;
  limit?: number;
  page?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

// Chart data interfaces
interface TimeSeriesData {
  timestamp: string;
  time: number;
  online: number;
  offline: number;
  maintenance: number;
  error: number;
  warning: number;
  total: number;
}

interface StatusDistribution {
  status: string;
  count: number;
  percentage: number;
}

interface DeviceSignalData {
  device_id: string;
  device_name: string;
  timestamp: string;
  time: number;
  ble_signal?: number;
  gsm_signal?: number;
  battery?: number;
  sensor?: boolean;
  blocked?: boolean;
  alarm_status?: number;
}

interface AlarmTrend {
  date: string;
  alarms: number;
  blocked: number;
  devices_with_alarms: number;
  devices_blocked: number;
}

interface DeviceState {
  device_id: string;
  device_name: string;
  latest_status: string;
  latest_timestamp: number;
  has_alarm: boolean;
  is_blocked: boolean;
  battery?: number;
  ble_signal?: number;
  gsm_signal?: number;
}

const batchSize = 100000; // reasonable batch size to balance network and memory

const DeviceLogs: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  
  // Color mode values
  const bg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // State management
  const [logs, setLogs] = useState<DeviceLog[]>([]);
  const [lastLogs, setLastLogs] = useState<DeviceLog[]>([]);
  const [analytics, setAnalytics] = useState<DeviceAnalytics | null>(null);
  const [devices, setDevices] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [sort_by, setSortBy] = useState<string>('');
  const [sort_direction, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [timeRange, setTimeRange] = useState<'1h' | '1d' | '1w' | '1m' | 'custom'>('1d');

  // Human-readable label for the selected range
  const timeRangeLabel = useMemo(() => {
    switch (timeRange) {
      case '1h':
        return 'Last 1h';
      case '1d':
        return 'Last 24h';
      case '1w':
        return 'Last 7d';
      case '1m':
        return 'Last 30d';
      case 'custom':
        return 'Selected Range';
      default:
        return '';
    }
  }, [timeRange]);

  // Filters state
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [selectedCompositeStatus, setSelectedCompositeStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(() => {
    const sevenDaysAgo = subDays(new Date(), 7);
    return format(sevenDaysAgo, 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return format(new Date(), 'yyyy-MM-dd');
  });
  
  // Cleanup modal
  const { isOpen: isCleanupOpen, onOpen: onCleanupOpen, onClose: onCleanupClose } = useDisclosure();
  const [cleanupDate, setCleanupDate] = useState(() => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return oneWeekAgo.toISOString().split('T')[0];
  });
  const [isCleanupLoading, setIsCleanupLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  // Chart colors - distinct colors for different states
  const chartColors = {
    online: '#22C55E',      // Green
    offline: '#EF4444',     // Red
    maintenance: '#F59E0B', // Amber
    error: '#DC2626',       // Dark Red
    warning: '#F97316',     // Orange
    busy: '#8B5CF6',        // Purple
    blocked: '#EC4899',     // Pink
    sensor: '#06B6D4',      // Cyan
    alarm: '#B91C1C',       // Dark Red
    ble: '#3B82F6',         // Blue
    gsm: '#10B981',         // Emerald
    battery: '#84CC16',     // Lime
  };

  // Additional Filters
  const [flagFilter, setFlagFilter] = useState<string>(''); // blocked / unblocked / sensor_on / sensor_off
  const [alarmFilter, setAlarmFilter] = useState<string>(''); // has_alarm / no_alarm
  const [batteryFilter, setBatteryFilter] = useState<string>(''); // low / medium / high

  // Fetch devices for filter dropdown
  const fetchDevices = useCallback(async () => {
    try {
      if (!token) return;
      apiClient.client.setToken(token);
      const response = await apiClient.devices.listPaginated(
        { page: 1, limit: 1000 }, 
        { tenant_id: isSystem ? undefined : user?.tenantId }
      );
      setDevices(response.data);
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  }, [token, isSystem, user?.tenantId]);

  // Build filters object
  const buildFilters = useCallback((): DeviceLogFilters => {
    const filterObj: DeviceLogFilters = {
      limit: pageSize,
      page: currentPage,
    };

    if (selectedDevice) filterObj.device_id = selectedDevice;
    
    // Handle composite status filtering
    if (selectedCompositeStatus) {
      switch (selectedCompositeStatus) {
        case 'blocked':
          filterObj.blocked = true;
          break;
        case 'unblocked':
          filterObj.blocked = false;
          break;
        case 'alarm_type_1':
          filterObj.has_alarm = true;
          // Note: API might need extension to filter by specific alarm levels
          break;
        case 'alarm_type_2':
          filterObj.has_alarm = true;
          break;
        case 'alarm_type_3':
          filterObj.has_alarm = true;
          break;
        case 'no_alarm':
          filterObj.has_alarm = false;
          break;
        case 'sensor_on':
          filterObj.sensor = true;
          // Note: API might need extension to filter by sensor state
          break;
        case 'sensor_off':
          filterObj.sensor = false
          // Note: API might need extension to filter by sensor state
          break;
        default:
          break;
      }
    }
    
    if (startDate) filterObj.start_time = startOfDay(new Date(startDate)).getTime();
    if (endDate) filterObj.end_time = endOfDay(new Date(endDate)).getTime();

    // Add sorting parameters
    if (sort_by) {
      filterObj.sort_by = sort_by;
      filterObj.sort_direction = sort_direction;
    }

    // Handle flag filter
    if (flagFilter) {
      switch (flagFilter) {
        case 'blocked':
          filterObj.blocked = true;
          break;
        case 'unblocked':
          filterObj.blocked = false;
          break;
        case 'sensor_on':
          filterObj.sensor = true;
          break;
        case 'sensor_off':
          filterObj.sensor = false;
          break;
        default:
          break;
      }
    }

    // Handle alarm filter
    if (alarmFilter) {
      if (alarmFilter === 'has_alarm') {
        filterObj.has_alarm = true;
      } else if (alarmFilter === 'no_alarm') {
        filterObj.has_alarm = false;
      }
    }

    return filterObj;
  }, [selectedDevice, selectedCompositeStatus, startDate, endDate, pageSize, currentPage, sort_by, sort_direction, flagFilter, alarmFilter]);

  // Fetch logs for table display
  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      const filters = buildFilters();
      const response = await apiClient.deviceLogs.list(filters);
      
      console.log(response.data)
      // Ensure each row has a unique key to avoid React duplicate key warnings
      const logsWithUid: DeviceLog[] = response.data.data.map((log: DeviceLog, idx: number) => ({
        ...log,
        uid: `${log.device_id}-${log.logged_at}-${idx}`,
      }));

      setLogs(logsWithUid);
      setTotalItems(response.data.pagination.total);
    } catch (error) {
      toast({
        title: 'Error fetching device logs',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, buildFilters, toast]);

  // Fetch last 24h logs for analytics
  const fetchLastLogs = useCallback(async () => {
    try {
      if (!token) return;
      apiClient.client.setToken(token);

      // Determine date range
      const now = new Date();
      let startTime: Date;
      let endTime: Date = now;

      switch (timeRange) {
        case '1h':
          startTime = subHours(now, 1);
          break;
        case '1d':
          startTime = subHours(now, 24);
          break;
        case '1w':
          startTime = subDays(now, 7);
          break;
        case '1m':
          startTime = subDays(now, 30);
          break;
        case 'custom':
          startTime = new Date(startDate);
          endTime = new Date(endDate);
          break;
        default:
          startTime = subHours(now, 24);
      }

      let page = 1;
      let fetchedAll: DeviceLog[] = [];

      while (true) {
        const pageFilters: DeviceLogFilters = {
          start_time: startTime.getTime(),
          end_time: endTime.getTime(),
          limit: batchSize,
          page,
          sort_by: 'logged_at',
          sort_direction: 'asc', // fetch oldest first to ensure covering full range
        };

        if (selectedDevice) pageFilters.device_id = selectedDevice;

        const resp = await apiClient.deviceLogs.list(pageFilters);
        const batch: DeviceLog[] = resp.data.data;

        if (!batch.length) break;

        fetchedAll = fetchedAll.concat(batch);

        if (batch.length < batchSize) {
          // last batch fetched
          break;
        }

        page += 1;

        // Safety guard: do not fetch unbounded data (max 100 pages)
        if (page > 100) {
          console.warn('Aborting fetchLastLogs early to avoid excessive data');
          break;
        }
      }

      // Attach uid to each log to keep consistency
      const logsWithUid: DeviceLog[] = fetchedAll.map((log, idx) => ({
        ...log,
        uid: `${log.device_id}-${log.logged_at}-${idx}`,
      }));

      console.log('Fetched timeline logs:', logsWithUid.length);
      setLastLogs(logsWithUid);
    } catch (error) {
      console.error('Error fetching timeline logs:', error);
    }
  }, [token, selectedDevice, timeRange, startDate, endDate]);

  // Fetch analytics for selected device
  const fetchAnalytics = useCallback(async () => {
    if (!selectedDevice || !token) return;
    
    setIsAnalyticsLoading(true);
    try {
      apiClient.client.setToken(token);
      const params = {
        start_time: startDate ? startOfDay(new Date(startDate)).getTime() : undefined,
        end_time: endDate ? endOfDay(new Date(endDate)).getTime() : undefined,
      };
      
      const response = await apiClient.deviceLogs.getAnalytics(selectedDevice, params);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setIsAnalyticsLoading(false);
    }
  }, [selectedDevice, startDate, endDate, token]);

  // Get latest state for each device
  const deviceStates = useMemo((): DeviceState[] => {
    if (!lastLogs.length) return [];

    const latestByDevice = new Map<string, DeviceLog>();
    
    // Find the latest log for each device
    lastLogs.forEach(log => {
      const existing = latestByDevice.get(log.device_id);
      if (!existing || log.logged_at > existing.logged_at) {
        latestByDevice.set(log.device_id, log);
      }
    });

    return Array.from(latestByDevice.values()).map(log => {
      const device = devices.find(d => d.id === log.device_id);
      return {
        device_id: log.device_id,
        device_name: device?.nick_name || device?.serial_no || 'Unknown',
        latest_status: log.status,
        latest_timestamp: log.logged_at,
        has_alarm: !!log.alarm_status,
        is_blocked: !!log.blocked,
        battery: log.battery,
        ble_signal: log.ble_signal,
        gsm_signal: log.gsm_signal,
      };
    });
  }, [lastLogs, devices]);

  // Process time series data for charts
  const timeSeriesData = useMemo((): TimeSeriesData[] => {
    if (!lastLogs.length) return [];

    // Group logs by hour for last 24h
    const hourlyData = new Map<string, { [key: string]: Set<string> }>();
    
    lastLogs.forEach(log => {
      const hour = format(new Date(log.logged_at), 'yyyy-MM-dd HH:00');
      if (!hourlyData.has(hour)) {
        hourlyData.set(hour, { 
          online: new Set(), 
          offline: new Set(), 
          maintenance: new Set(), 
          error: new Set(),
          warning: new Set(),
          total: new Set()
        });
      }
      
      const data = hourlyData.get(hour)!;
      const status = log.status.toLowerCase();
      
      // Count unique devices in each status
      if (status in data) {
        data[status].add(log.device_id);
      } else if (status.includes('warn')) {
        data.warning.add(log.device_id);
      } else {
        data.error.add(log.device_id);
      }
      data.total.add(log.device_id);
    });

    return Array.from(hourlyData.entries())
      .map(([timestamp, data]) => ({
        timestamp: format(new Date(timestamp), 'MMM dd HH:mm'),
        time: new Date(timestamp).getTime(),
        online: data.online.size,
        offline: data.offline.size,
        maintenance: data.maintenance.size,
        error: data.error.size,
        warning: data.warning.size,
        total: data.total.size,
      }))
      .sort((a, b) => a.time - b.time);
  }, [lastLogs]);

  // Process status distribution data from latest device states
  const statusDistribution = useMemo((): StatusDistribution[] => {
    if (!deviceStates.length) return [];

    const statusCounts = deviceStates.reduce((acc, device) => {
      const status = device.latest_status.toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = deviceStates.length;
    return Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
      percentage: Math.round((count / total) * 100),
    }));
  }, [deviceStates]);

  // Process signal and battery data
  const signalData = useMemo((): DeviceSignalData[] => {
    if (!lastLogs.length) return [];

    if (selectedDevice) {
      // Single device: show detailed timeline
      return lastLogs
        .filter(log => log.device_id === selectedDevice)
        .map(log => {
          const device = devices.find(d => d.id === log.device_id);
          return {
            device_id: log.device_id,
            device_name: device?.nick_name || 'Unknown',
            timestamp: format(new Date(log.logged_at), 'MMM dd HH:mm'),
            time: log.logged_at,
            ble_signal: log.ble_signal,
            gsm_signal: log.gsm_signal,
            battery: log.battery,
            sensor: log.sensor,
            blocked: log.blocked,
            alarm_status: log.alarm_status,
          };
        })
        .sort((a, b) => a.time - b.time);
    } else {
      // All devices: show latest readings for each device
      const latestByDevice = new Map<string, DeviceLog>();
      lastLogs.forEach(log => {
        if (log.ble_signal || log.gsm_signal || log.battery) {
          const existing = latestByDevice.get(log.device_id);
          if (!existing || log.logged_at > existing.logged_at) {
            latestByDevice.set(log.device_id, log);
          }
        }
      });

      return Array.from(latestByDevice.values()).map(log => {
        const device = devices.find(d => d.id === log.device_id);
        return {
          device_id: log.device_id,
          device_name: device?.nick_name || device?.serial_no || 'Unknown',
          timestamp: format(new Date(log.logged_at), 'MMM dd HH:mm'),
          time: log.logged_at,
          ble_signal: log.ble_signal,
          gsm_signal: log.gsm_signal,
          battery: log.battery,
          sensor: log.sensor,
          blocked: log.blocked,
          alarm_status: log.alarm_status,
        };
      }).sort((a, b) => a.device_name.localeCompare(b.device_name));
    }
  }, [lastLogs, selectedDevice, devices]);

  // Process alarm trends from device states
  const alarmTrends = useMemo((): AlarmTrend[] => {
    if (!lastLogs.length) return [];

    const dailyData = new Map<string, { 
      alarms: Set<string>; 
      blocked: Set<string>; 
      devices_with_alarms: Set<string>;
      devices_blocked: Set<string>;
    }>();
    
    lastLogs.forEach(log => {
      const date = format(new Date(log.logged_at), 'yyyy-MM-dd');
      if (!dailyData.has(date)) {
        dailyData.set(date, { 
          alarms: new Set(), 
          blocked: new Set(),
          devices_with_alarms: new Set(),
          devices_blocked: new Set()
        });
      }
      
      const data = dailyData.get(date)!;
      if (log.alarm_status) {
        data.alarms.add(`${log.device_id}-${log.logged_at}`);
        data.devices_with_alarms.add(log.device_id);
      }
      if (log.blocked) {
        data.blocked.add(`${log.device_id}-${log.logged_at}`);
        data.devices_blocked.add(log.device_id);
      }
    });

    return Array.from(dailyData.entries())
      .map(([date, data]) => ({
        date: format(new Date(date), 'MMM dd'),
        alarms: data.alarms.size,
        blocked: data.blocked.size,
        devices_with_alarms: data.devices_with_alarms.size,
        devices_blocked: data.devices_blocked.size,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [lastLogs]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchLogs();
      fetchLastLogs();
      if (selectedDevice) fetchAnalytics();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs, fetchLastLogs, fetchAnalytics, selectedDevice]);

  // Initial data fetch
  useEffect(() => {
    if (token) {
      fetchDevices();
      fetchLogs();
      fetchLastLogs();
    }
  }, [token, fetchDevices, fetchLogs, fetchLastLogs]);

  // Fetch analytics when device or date range changes
  useEffect(() => {
    if (selectedDevice) {
      fetchAnalytics();
    } else {
      setAnalytics(null);
    }
    fetchLastLogs(); // Refresh 24h data when device selection changes
  }, [selectedDevice, startDate, endDate, fetchAnalytics, fetchLastLogs]);

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedDevice('');
    setSelectedCompositeStatus('');
    setStartDate(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
    setEndDate(format(new Date(), 'yyyy-MM-dd'));
    setSortBy('');
    setSortDirection('desc');
    setCurrentPage(1);
    setFlagFilter('');
    setAlarmFilter('');
    setBatteryFilter('');
  };

  // Cleanup logs
  const handleCleanup = async () => {
    setIsCleanupLoading(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      const cutoffTime = new Date(cleanupDate).getTime();
      const response = await apiClient.deviceLogs.cleanup({ older_than: cutoffTime });
      
      toast({
        title: 'Cleanup Completed',
        description: `Deleted ${response.data.deleted_count} log entries`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      onCleanupClose();
      fetchLogs();
      fetchLastLogs();
      if (selectedDevice) fetchAnalytics();
    } catch (error) {
      toast({
        title: 'Cleanup Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCleanupLoading(false);
    }
  };

  // Download logs as XLSX
  const handleDownload = useCallback(async () => {
    setIsDownloadLoading(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);

      // Build current filters and pass them to the download endpoint
      let filters: DeviceLogFilters = {
        limit: batchSize
      }
      if (selectedDevice) {
        filters.device_id = selectedDevice;
      }
      // if (startDate) {
      //   filters.start_time = new Date(startDate).getTime();
      // }
      // if (endDate) {
      //   filters.end_time = new Date(endDate).getTime();
      // }

      // The api is expected to return a binary (xlsx) payload
      const response = await apiClient.deviceLogs.download(filters);
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadLoading(false);
    }
  }, [token, buildFilters, toast]);

  // Status color mapping
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'active':
        return 'green';
      case 'offline':
      case 'inactive':
        return 'red';
      case 'maintenance':
        return 'yellow';
      case 'warning':
        return 'orange';
      case 'error':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Source color mapping
  const getSourceColor = (source?: string) => {
    switch (source) {
      case 'MQTT':
        return 'orange';
      case 'API':
        return 'purple';
      case 'BLE':
        return 'blue';
      case 'MANUAL':
        return 'teal';
      default:
        return 'gray';
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  // Format battery level
  const formatBattery = (battery?: number) => {
    if (!battery) return '-';
    const percentage = calculateBatteryPercentage(battery); // Assuming 4.2V is 100%
    return `${battery}mV (${percentage}%)`;
  };

  // Format signal strength
  const formatSignal = (signal?: number) => {
    if (!signal) return '-';
    return `${signal}dBm`;
  };

  // Get battery health color
  const getBatteryHealthColor = (battery?: number) => {
    if (!battery) return 'gray';
    const percentage = calculateBatteryPercentage(battery)
    if (percentage > 80) return 'green';
    if (percentage > 50) return 'yellow';
    if (percentage > 20) return 'orange';
    return 'red';
  };

  const calculateBatteryPercentage = (voltage: number) : number => {
    const percentage = Math.round(((voltage - 5000) / (6000 - 5000)) * 100);
    return percentage > 100? 100 : percentage;
  }

  // Table columns
  const columns: Column<DeviceLog>[] = [
    {
      header: 'Timestamp',
      accessor: (log) => (
        <Text fontSize="sm" fontFamily="mono">
          {formatTimestamp(log.logged_at)}
        </Text>
      ),
    },
    {
      header: 'Device',
      accessor: (log) => {
        const device = devices.find(d => d.id === log.device_id);
        return (
          <VStack align="start" spacing={0}>
            <Text fontSize="sm" fontWeight="medium">
              {device?.nick_name || 'Unknown'}
            </Text>
            <Text fontSize="xs" color="gray.500" fontFamily="mono">
              {log.device_id}
            </Text>
          </VStack>
        );
      },
    },
    {
      header: 'Status',
      accessor: (log) => (
        <Badge colorScheme={getStatusColor(log.status)}>
          {log.status}
        </Badge>
      ),
    },
    {
      header: 'Flags',
      accessor: (log) => (
        <HStack spacing={1}>
          {log.busy && <Badge size="sm" colorScheme="purple">Busy</Badge>}
          {log.blocked && <Badge size="sm" colorScheme="pink">Blocked</Badge>}
          {log.sensor && <Badge size="sm" colorScheme="cyan">Sensor</Badge>}
          {log.alarm_status && <Badge size="sm" colorScheme="red">Alarm</Badge>}
        </HStack>
      ),
    },
    {
      header: 'Battery',
      accessor: (log) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" fontFamily="mono">
            {formatBattery(log.battery)}
          </Text>
          {log.battery && (
            <Progress
              value={(log.battery / 4200) * 100}
              size="sm"
              colorScheme={getBatteryHealthColor(log.battery)}
              width="60px"
            />
          )}
        </VStack>
      ),
    },
    {
      header: 'Signals',
      accessor: (log) => (
        <VStack align="start" spacing={0}>
          {log.ble_signal && (
            <Text fontSize="xs">BLE: {formatSignal(log.ble_signal)}</Text>
          )}
          {log.gsm_signal && (
            <Text fontSize="xs">GSM: {formatSignal(log.gsm_signal)}</Text>
          )}
        </VStack>
      ),
    },
    {
      header: 'Source',
      accessor: (log) => (
        <Badge colorScheme={getSourceColor(log.source)} size="sm">
          {log.source || 'unknown'}
        </Badge>
      ),
    },
    {
      header: 'Alarm',
      accessor: (log) => (
        log.alarm_description ? (
          <Tooltip label={log.alarm_description}>
            <Badge colorScheme="red" size="sm">
              {log.alarm_status}
            </Badge>
          </Tooltip>
        ) : (
          <Text fontSize="sm" color="gray.500">-</Text>
        )
      ),
    },
  ];

  // Analytics cards
  const analyticsCards = useMemo(() => {
    if (!analytics) return [];
    
    return [
      {
        label: 'Total Logs',
        value: analytics.total_logs.toLocaleString(),
        icon: FiActivity,
        color: 'blue',
      },
      {
        label: 'Alarm Count',
        value: analytics.alarm_count?.toLocaleString() || '0',
        icon: FiAlertTriangle,
        color: 'red',
      },
      {
        label: 'Blocked Count',
        value: analytics.blocked_count?.toLocaleString() || '0',
        icon: FiWifi,
        color: 'orange',
      },
      {
        label: 'Last Activity',
        value: analytics.last_activity 
          ? formatTimestamp(typeof analytics.last_activity === 'string' 
              ? new Date(analytics.last_activity).getTime() 
              : analytics.last_activity)
          : '-',
        icon: FiClock,
        color: 'green',
      },
    ];
  }, [analytics]);

  // Quick stats from latest device states (last 24h)
  const quickStats = useMemo(() => {
    const totalDevices = deviceStates.length;
    const onlineDevices = deviceStates.filter(d => d.latest_timestamp > Date.now() - 1000 * 60 * 6).length;
    const offlineDevices = deviceStates.filter(d => d.latest_timestamp < Date.now() - 1000 * 60 * 6).length;
    const alarmedDevices = deviceStates.filter(d => d.has_alarm).length;
    const blockedDevices = deviceStates.filter(d => d.is_blocked).length;
    const devicesWithBattery = deviceStates.filter(d => d.battery);
    const avgBattery = devicesWithBattery.length > 0 
      ? Math.round(devicesWithBattery.reduce((sum, d) => sum + (d.battery || 0), 0) / devicesWithBattery.length)
      : 0;

    return {
      totalDevices,
      onlineDevices,
      offlineDevices,
      alarmedDevices,
      blockedDevices,
      avgBattery,
      alarmRate: totalDevices > 0 ? Math.round((alarmedDevices / totalDevices) * 100) : 0,
      uptime: totalDevices > 0 ? Math.round((onlineDevices / totalDevices) * 100) : 0,
    };
  }, [deviceStates]);

  // Process device state distribution for critical states
  const deviceStateDistribution = useMemo(() => {
    if (!deviceStates.length) return {
      blocked: { blocked: 0, unblocked: 0 },
      sensor: { active: 0, inactive: 0 },
      alarm: { none: 0, level1: 0, level2: 0, level3: 0 },
      battery: { healthy: 0, warning: 0, critical: 0, unknown: 0 }
    };

    const distribution = {
      blocked: { blocked: 0, unblocked: 0 },
      sensor: { active: 0, inactive: 0 },
      alarm: { none: 0, level1: 0, level2: 0, level3: 0 },
      battery: { healthy: 0, warning: 0, critical: 0, unknown: 0 }
    };

    deviceStates.forEach(device => {
      // Blocked state
      if (device.is_blocked) {
        distribution.blocked.blocked++;
      } else {
        distribution.blocked.unblocked++;
      }

      // Sensor state (assuming sensor boolean indicates if sensor is active)
      const latestLog = lastLogs.find(log => 
        log.device_id === device.device_id && 
        log.logged_at === device.latest_timestamp
      );
      if (latestLog?.sensor) {
        distribution.sensor.active++;
      } else {
        distribution.sensor.inactive++;
      }

      // Alarm levels
      if (device.has_alarm) {
        const alarmLevel = latestLog?.alarm_status || 1;
        if (alarmLevel === 1) distribution.alarm.level1++;
        else if (alarmLevel === 2) distribution.alarm.level2++;
        else if (alarmLevel === 3) distribution.alarm.level3++;
      } else {
        distribution.alarm.none++;
      }

      // Battery health
      if (device.battery) {
        const percentage = calculateBatteryPercentage(device.battery);
        if (percentage > 50) distribution.battery.healthy++;
        else if (percentage > 20) distribution.battery.warning++;
        else distribution.battery.critical++;
      } else {
        distribution.battery.unknown++;
      }
    });

    return distribution;
  }, [deviceStates, lastLogs]);

  // Process multi-device timeline data for health overview
  const multiDeviceTimeline = useMemo(() => {
    console.log("LASTLOGS", lastLogs)
    if (!lastLogs.length) return [];

    // Group logs by hour (floor timestamp) and aggregate device states
    const hourlyData = new Map<number, {
      timestamp: string;
      time: number;
      avgBattery: number;
      batteryCount: number;
      blockedDevices: Set<string>;
      sensorActiveDevices: Set<string>;
      alarmDevices: Set<string>;
      totalDevices: Set<string>;
      batteryReadings: number[];
    }>();

    lastLogs.forEach(log => {
      const hourStartMs = Math.floor(log.logged_at / 3600000) * 3600000; // start of hour
      if (!hourlyData.has(hourStartMs)) {
        const hourDate = new Date(hourStartMs);
        hourlyData.set(hourStartMs, {
          timestamp: format(hourDate, 'MMM dd HH:mm'),
          time: hourStartMs,
          avgBattery: 0,
          batteryCount: 0,
          blockedDevices: new Set(),
          sensorActiveDevices: new Set(),
          alarmDevices: new Set(),
          totalDevices: new Set(),
          batteryReadings: [],
        });
      }

      const data = hourlyData.get(hourStartMs)!;
      data.totalDevices.add(log.device_id);

      if (log.battery) {
        data.batteryReadings.push(log.battery);
      }
      if (log.blocked) {
        data.blockedDevices.add(log.device_id);
      }
      if (log.sensor) {
        data.sensorActiveDevices.add(log.device_id);
      }
      if (log.alarm_status) {
        data.alarmDevices.add(log.device_id);
      }
    });

    console.log("HOURLYDATA", hourlyData.size)
    return Array.from(hourlyData.values())
      .map(data => ({
        timestamp: data.timestamp,
        time: data.time,
        avgBattery: data.batteryReadings.length > 0 
          ? Math.round(data.batteryReadings.reduce((sum, b) => sum + b, 0) / data.batteryReadings.length)
          : null,
        blockedRate: data.totalDevices.size > 0 
          ? Math.round((data.blockedDevices.size / data.totalDevices.size) * 100) 
          : 0,
        sensorActiveRate: data.totalDevices.size > 0 
          ? Math.round((data.sensorActiveDevices.size / data.totalDevices.size) * 100) 
          : 0,
        alarmRate: data.totalDevices.size > 0 
          ? Math.round((data.alarmDevices.size / data.totalDevices.size) * 100) 
          : 0,
        totalDevices: data.totalDevices.size,
        blockedCount: data.blockedDevices.size,
        sensorActiveCount: data.sensorActiveDevices.size,
        alarmCount: data.alarmDevices.size,
      }))
      .sort((a, b) => a.time - b.time);
  }, [lastLogs]);

  // Process problem patterns - devices with recurring issues
  const problemPatterns = useMemo(() => {
    if (!lastLogs.length) return [];

    const deviceIssues = new Map<string, {
      device_id: string;
      device_name: string;
      alarmCount: number;
      blockedCount: number;
      lowBatteryCount: number;
      offlineCount: number;
      lastSeen: number;
      issueScore: number;
    }>();

    lastLogs.forEach(log => {
      const device = devices.find(d => d.id === log.device_id);
      const deviceName = device?.nick_name || device?.serial_no || 'Unknown';
      
      if (!deviceIssues.has(log.device_id)) {
        deviceIssues.set(log.device_id, {
          device_id: log.device_id,
          device_name: deviceName,
          alarmCount: 0,
          blockedCount: 0,
          lowBatteryCount: 0,
          offlineCount: 0,
          lastSeen: log.logged_at,
          issueScore: 0,
        });
      }

      const issues = deviceIssues.get(log.device_id)!;
      if (log.logged_at > issues.lastSeen) {
        issues.lastSeen = log.logged_at;
      }

      if (log.alarm_status) issues.alarmCount++;
      if (log.blocked) issues.blockedCount++;
      if (log.battery && (log.battery / 6000) * 100 < 30) issues.lowBatteryCount++;
      if (log.status.toLowerCase() === 'offline') issues.offlineCount++;
    });

    return Array.from(deviceIssues.values())
      .map(device => ({
        ...device,
        issueScore: (device.alarmCount * 3) + (device.lowBatteryCount * 1) + (device.offlineCount * 2),
      }))
      .filter(device => device.issueScore > 0)
      .sort((a, b) => b.issueScore - a.issueScore)
      .slice(0, 10); // Top 10 problematic devices
  }, [lastLogs, devices]);

  // Apply battery filter to logs before displaying in table
  const displayedLogs = useMemo(() => {
    if (!batteryFilter) return logs;

    return logs.filter((log) => {
      if (!log.battery) return false;
      const percentage = calculateBatteryPercentage(log.battery);
      console.log("BATTERY PERCENTAGE", percentage)
      switch (batteryFilter) {
        case 'low':
          return percentage < 40;
        case 'medium':
          return percentage >= 40 && percentage <= 80;
        case 'high':
          return percentage > 80;
        default:
          return true;
      }
    });
  }, [logs, batteryFilter]);

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">
          {t('deviceLogs.title')}
        </Heading>
        <HStack spacing={3}>
          <Button
            size="sm"
            variant={autoRefresh ? "solid" : "outline"}
            colorScheme="blue"
            leftIcon={<FiRefreshCw />}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto Refresh
          </Button>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FiRefreshCw />}
            onClick={() => {
              fetchLogs();
              fetchLastLogs();
              if (selectedDevice) fetchAnalytics();
            }}
            isLoading={isLoading}
          >
            Refresh
          </Button>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FiDownload />}
            onClick={handleDownload}
            isLoading={isDownloadLoading}
          >
            Download
          </Button>
          {isSystem && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              leftIcon={<FiTrash2 />}
              onClick={onCleanupOpen}
            >
              Cleanup
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Quick Stats Overview - Latest Device States (Last 24h) */}
      <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={4} mb={6}>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">Total Devices</StatLabel>
              <StatNumber fontSize="lg" color="blue.500">{quickStats.totalDevices}</StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">Online</StatLabel>
              <StatNumber fontSize="lg" color="green.500">{quickStats.onlineDevices}</StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">Offline</StatLabel>
              <StatNumber fontSize="lg" color="red.500">{quickStats.offlineDevices}</StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">With Alarms</StatLabel>
              <StatNumber fontSize="lg" color="red.500">{quickStats.alarmedDevices}</StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">Blocked</StatLabel>
              <StatNumber fontSize="lg" color="pink.500">{quickStats.blockedDevices}</StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">Uptime</StatLabel>
              <StatNumber fontSize="lg" color="green.500">{quickStats.uptime}%</StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">Alarm Rate</StatLabel>
              <StatNumber fontSize="lg" color="red.500">{quickStats.alarmRate}%</StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody textAlign="center">
            <Stat>
              <StatLabel fontSize="xs">Avg Battery</StatLabel>
              <StatNumber fontSize="lg" color={getBatteryHealthColor(quickStats.avgBattery)}>
                {quickStats.avgBattery}mV
              </StatNumber>
              <StatHelpText fontSize="xs">{timeRangeLabel}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Analytics Cards for Selected Device */}
      {selectedDevice && (
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} mb={6}>
          {isAnalyticsLoading ? (
            <GridItem colSpan={4}>
              <Flex justify="center" align="center" h="100px">
                <Spinner />
              </Flex>
            </GridItem>
          ) : (
            analyticsCards.map((card, index) => (
              <GridItem key={index}>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>
                        <HStack>
                          <card.icon />
                          <Text>{card.label}</Text>
                        </HStack>
                      </StatLabel>
                      <StatNumber color={`${card.color}.500`}>
                        {card.value}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </GridItem>
            ))
          )}
        </Grid>
      )}

      {/* Filters */}
      <Card mb={6}>
        <CardHeader>
          <HStack justify="space-between">
            <HStack>
              <FiFilter />
              <Text fontWeight="medium">Filters</Text>
              <Text fontSize="sm" color="gray.500">
                (Stats: {timeRangeLabel} | Table: Last {pageSize} logs)
              </Text>
            </HStack>
            <Button size="sm" variant="ghost" onClick={clearFilters}>
              Clear All
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
            <FormControl>
              <FormLabel fontSize="sm">Device</FormLabel>
              <Select
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
                placeholder="All devices"
                size="sm"
              >
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.nick_name || device.serial_no}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Composite Status</FormLabel>
              <Select
                value={selectedCompositeStatus}
                onChange={(e) => setSelectedCompositeStatus(e.target.value)}
                placeholder="All device states"
                size="sm"
              >
                <option value="blocked">Blocked</option>
                <option value="unblocked">Unblocked</option>
                <option value="alarm_type_1">Alarm Type 1</option>
                <option value="alarm_type_2">Alarm Type 2</option>
                <option value="alarm_type_3">Alarm Type 3</option>
                <option value="no_alarm">No Alarm</option>
                <option value="sensor_on">Sensor On</option>
                <option value="sensor_off">Sensor Off</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Time Range</FormLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as any)}
                size="sm"
              >
                <option value="1h">Last 1h</option>
                <option value="1d">Last 24h</option>
                <option value="1w">Last 7d</option>
                <option value="1m">Last 30d</option>
                <option value="custom">Custom</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Start Date</FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                isDisabled={timeRange !== 'custom'}
                size="sm"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">End Date</FormLabel>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                isDisabled={timeRange !== 'custom'}
                size="sm"
              />
            </FormControl>

            {/* Flag Filter */}
            <FormControl>
              <FormLabel fontSize="sm">Flag</FormLabel>
              <Select
                value={flagFilter}
                onChange={(e) => setFlagFilter(e.target.value)}
                placeholder="All flags"
                size="sm"
              >
                <option value="blocked">Blocked</option>
                <option value="unblocked">Unblocked</option>
                <option value="sensor_on">Sensor On</option>
                <option value="sensor_off">Sensor Off</option>
              </Select>
            </FormControl>

            {/* Alarm Filter */}
            <FormControl>
              <FormLabel fontSize="sm">Alarm</FormLabel>
              <Select
                value={alarmFilter}
                onChange={(e) => setAlarmFilter(e.target.value)}
                placeholder="All alarms"
                size="sm"
              >
                <option value="has_alarm">With Alarm</option>
                <option value="no_alarm">No Alarm</option>
              </Select>
            </FormControl>

            {/* Battery Filter */}
            <FormControl>
              <FormLabel fontSize="sm">Battery</FormLabel>
              <Select
                value={batteryFilter}
                onChange={(e) => setBatteryFilter(e.target.value)}
                placeholder="All levels"
                size="sm"
              >
                <option value="low">Low (&lt;40%)</option>
                <option value="medium">Medium (40-80%)</option>
                <option value="high">High (&gt;80%)</option>
              </Select>
            </FormControl>
          </Grid>
        </CardBody>
      </Card>

      {/* Data Visualization Tabs */}
      <Tabs variant="enclosed" mb={6}>
        <TabList>
          <Tab><FiTrendingUp /> &nbsp; Timeline</Tab>
          <Tab><FiPieChart /> &nbsp; Device States</Tab>
          <Tab><FiBattery /> &nbsp; Health Monitoring</Tab>
          <Tab><FiAlertTriangle /> &nbsp; Problem Analysis</Tab>
        </TabList>

        <TabPanels>
          {/* Fleet Timeline Chart */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Health Timeline ({timeRangeLabel})</Heading>
                <Text fontSize="sm" color="gray.500">
                  Aggregated view of device states and battery health over time
                </Text>
              </CardHeader>
              <CardBody>
                <Box h="400px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={multiDeviceTimeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="timestamp" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis yAxisId="left" label={{ value: 'Battery (mV)', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" label={{ value: 'Rate (%)', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip 
                        formatter={(value: any, name: string) => {
                          if (name.includes('Rate')) return [`${value}%`, name];
                          if (name.includes('Battery')) return [`${value}mV`, name];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="avgBattery"
                        fill={chartColors.battery}
                        fillOpacity={0.6}
                        name="Avg Battery (mV)"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="alarmRate"
                        stroke={chartColors.alarm}
                        strokeWidth={3}
                        dot={{ fill: chartColors.alarm, strokeWidth: 2, r: 4 }}
                        name="Alarm Rate (%)"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="blockedRate"
                        stroke={chartColors.blocked}
                        strokeWidth={3}
                        dot={{ fill: chartColors.blocked, strokeWidth: 2, r: 4 }}
                        name="Blocked Rate (%)"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="sensorActiveRate"
                        stroke={chartColors.sensor}
                        strokeWidth={2}
                        dot={{ fill: chartColors.sensor, strokeWidth: 2, r: 3 }}
                        name="Sensor Active Rate (%)"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Device States Distribution */}
          <TabPanel>
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
              <Card>
                <CardHeader>
                  <Heading size="md">Critical Device States</Heading>
                  <Text fontSize="sm" color="gray.500">
                    Current distribution of important device states
                  </Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6}>
                    {/* Blocked State */}
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="medium" mb={2}>Blocked Status</Text>
                      <HStack spacing={4}>
                        <Box flex={1}>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Unblocked</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.blocked.unblocked}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.blocked.unblocked / 
                              (deviceStateDistribution.blocked.blocked + deviceStateDistribution.blocked.unblocked)) * 100}
                            colorScheme="green"
                            size="sm"
                          />
                        </Box>
                        <Box flex={1}>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Blocked</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.blocked.blocked}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.blocked.blocked / 
                              (deviceStateDistribution.blocked.blocked + deviceStateDistribution.blocked.unblocked)) * 100}
                            colorScheme="red"
                            size="sm"
                          />
                        </Box>
                      </HStack>
                    </Box>

                    {/* Sensor State */}
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="medium" mb={2}>Sensor Status</Text>
                      <HStack spacing={4}>
                        <Box flex={1}>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Active</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.sensor.active}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.sensor.active / 
                              (deviceStateDistribution.sensor.active + deviceStateDistribution.sensor.inactive)) * 100}
                            colorScheme="cyan"
                            size="sm"
                          />
                        </Box>
                        <Box flex={1}>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Inactive</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.sensor.inactive}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.sensor.inactive / 
                              (deviceStateDistribution.sensor.active + deviceStateDistribution.sensor.inactive)) * 100}
                            colorScheme="gray"
                            size="sm"
                          />
                        </Box>
                      </HStack>
                    </Box>

                    {/* Battery Health */}
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="medium" mb={2}>Battery Health</Text>
                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Healthy (&gt;50%)</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.battery.healthy}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.battery.healthy / deviceStates.length) * 100}
                            colorScheme="green"
                            size="sm"
                          />
                        </Box>
                        <Box>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Warning (20-50%)</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.battery.warning}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.battery.warning / deviceStates.length) * 100}
                            colorScheme="yellow"
                            size="sm"
                          />
                        </Box>
                        <Box>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Critical (&lt;20%)</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.battery.critical}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.battery.critical / deviceStates.length) * 100}
                            colorScheme="red"
                            size="sm"
                          />
                        </Box>
                        <Box>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="xs">Unknown</Text>
                            <Text fontSize="xs" fontWeight="bold">
                              {deviceStateDistribution.battery.unknown}
                            </Text>
                          </Flex>
                          <Progress
                            value={(deviceStateDistribution.battery.unknown / deviceStates.length) * 100}
                            colorScheme="gray"
                            size="sm"
                          />
                        </Box>
                      </SimpleGrid>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <Heading size="md">Alarm Level Distribution</Heading>
                  <Text fontSize="sm" color="gray.500">
                    Current alarm status across all devices
                  </Text>
                </CardHeader>
                <CardBody>
                  <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'No Alarm', value: deviceStateDistribution.alarm.none, color: chartColors.online },
                            { name: 'Level 1', value: deviceStateDistribution.alarm.level1, color: chartColors.warning },
                            { name: 'Level 2', value: deviceStateDistribution.alarm.level2, color: chartColors.error },
                            { name: 'Level 3', value: deviceStateDistribution.alarm.level3, color: chartColors.alarm },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[
                            { name: 'No Alarm', value: deviceStateDistribution.alarm.none, color: chartColors.online },
                            { name: 'Level 1', value: deviceStateDistribution.alarm.level1, color: chartColors.warning },
                            { name: 'Level 2', value: deviceStateDistribution.alarm.level2, color: chartColors.error },
                            { name: 'Level 3', value: deviceStateDistribution.alarm.level3, color: chartColors.alarm },
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>
            </Grid>
          </TabPanel>

          {/* Health Monitoring - Single Device or Fleet */}
          <TabPanel>
            {selectedDevice ? (
              // Single device: detailed timeline
              <VStack spacing={6}>
                <Card w="full">
                  <CardHeader>
                    <Heading size="md">Device Health Timeline</Heading>
                    <Text fontSize="sm" color="gray.500">
                      Detailed health metrics for selected device
                    </Text>
                  </CardHeader>
                  <CardBody>
                    <Box h="400px">
                      <ResponsiveContainer width="100%" height="100%" minHeight="200px">
                        <ComposedChart data={signalData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="timestamp" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis yAxisId="left" label={{ value: 'Battery (mV)', angle: -90, position: 'insideLeft' }} />
                          <YAxis yAxisId="right" orientation="right" domain={[0, 1]} label={{ value: 'State (0/1)', angle: 90, position: 'insideRight' }} />
                          <RechartsTooltip 
                            formatter={(value: any, name: string) => {
                              if (name.includes('Battery')) return [`${value}mV`, name];
                              if (name.includes('Signal')) return [`${value}dBm`, name];
                              return [value ? 'Active' : 'Inactive', name];
                            }}
                          />
                          <Legend />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="battery"
                            stroke={chartColors.battery}
                            strokeWidth={3}
                            dot={{ fill: chartColors.battery, strokeWidth: 2, r: 4 }}
                            name="Battery Voltage"
                          />
                          <Line
                            yAxisId="right"
                            type="stepAfter"
                            dataKey="blocked"
                            stroke={chartColors.blocked}
                            strokeWidth={2}
                            dot={{ fill: chartColors.blocked, strokeWidth: 2, r: 3 }}
                            name="Blocked State"
                          />
                          <Line
                            yAxisId="right"
                            type="stepAfter"
                            dataKey="sensor"
                            stroke={chartColors.sensor}
                            strokeWidth={2}
                            dot={{ fill: chartColors.sensor, strokeWidth: 2, r: 3 }}
                            name="Sensor Active"
                          />
                          <Line
                            yAxisId="right"
                            type="stepAfter"
                            dataKey="alarm_status"
                            stroke={chartColors.alarm}
                            strokeWidth={2}
                            dot={{ fill: chartColors.alarm, strokeWidth: 2, r: 3 }}
                            name="Alarm Level"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>

                <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} w="full">
                  <Card>
                    <CardHeader>
                      <Heading size="md">Signal Strength</Heading>
                    </CardHeader>
                    <CardBody>
                      <Box h="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={signalData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="timestamp" 
                              tick={{ fontSize: 12 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis />
                            <RechartsTooltip />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="ble_signal"
                              stroke={chartColors.ble}
                              strokeWidth={2}
                              dot={{ fill: chartColors.ble, strokeWidth: 2, r: 3 }}
                              name="BLE Signal (dBm)"
                            />
                            <Line
                              type="monotone"
                              dataKey="gsm_signal"
                              stroke={chartColors.gsm}
                              strokeWidth={2}
                              dot={{ fill: chartColors.gsm, strokeWidth: 2, r: 3 }}
                              name="GSM Signal (dBm)"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <Heading size="md">State Changes</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        {signalData.slice(-10).map((data, index) => (
                          <Box key={index} p={3} borderWidth={1} borderRadius="md">
                            <HStack justify="space-between">
                              <Text fontSize="sm" fontFamily="mono">{data.timestamp}</Text>
                              <HStack spacing={2}>
                                {data.blocked && <Badge colorScheme="red" size="sm">Blocked</Badge>}
                                {data.sensor && <Badge colorScheme="cyan" size="sm">Sensor</Badge>}
                                {data.alarm_status && <Badge colorScheme="red" size="sm">Alarm {data.alarm_status}</Badge>}
                                {data.battery && (
                                  <Badge colorScheme={getBatteryHealthColor(data.battery)} size="sm">
                                    {Math.round((data.battery / 4200) * 100)}%
                                  </Badge>
                                )}
                              </HStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>
                </Grid>
              </VStack>
            ) : (
              // Fleet overview
              <VStack spacing={6}>
                <Card w="full">
                  <CardHeader>
                    <Heading size="md">Battery Distribution</Heading>
                    <Text fontSize="sm" color="gray.500">
                      Current battery levels across all devices
                    </Text>
                  </CardHeader>
                  <CardBody>
                    <Box h="400px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={signalData.filter(d => d.battery).sort((a, b) => (b.battery || 0) - (a.battery || 0))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="device_name" 
                            tick={{ fontSize: 10 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis />
                          <RechartsTooltip 
                            formatter={(value: any) => [`${value}mV (${Math.round((value / 4200) * 100)}%)`, 'Battery']}
                          />
                          <Bar
                            dataKey="battery"
                            fill={chartColors.battery}
                            name="Battery Voltage (mV)"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>

                <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} w="full">
                  <Card>
                    <CardHeader>
                      <Heading size="md">Signal Quality Overview</Heading>
                    </CardHeader>
                    <CardBody>
                      <Box w="full">
                        <Text fontSize="sm" mb={2}>Signal Distribution</Text>
                        <Box h="400px">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={signalData.filter(d => d.ble_signal || d.gsm_signal).sort((a, b) => {
                              const aSignal = a.ble_signal || a.gsm_signal || 0;
                              const bSignal = b.ble_signal || b.gsm_signal || 0;
                              return bSignal - aSignal;
                            })}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="device_name" 
                                tick={{ fontSize: 10 }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                              />
                              <YAxis />
                              <RechartsTooltip formatter={(value: any, name: string) => [`${value}dBm`, name]} />
                              <Legend />
                              <Bar dataKey="ble_signal" name="BLE Signal" fill={chartColors.ble} />
                              <Bar dataKey="gsm_signal" name="GSM Signal" fill={chartColors.gsm} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <Heading size="md">Device Status Summary</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={3} align="stretch">
                        {deviceStates.slice(0, 8).map((device) => (
                          <Box key={device.device_id} p={3} borderWidth={1} borderRadius="md">
                            <HStack justify="space-between">
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="medium">{device.device_name}</Text>
                                <Text fontSize="xs" color="gray.500">{device.device_id}</Text>
                              </VStack>
                              <HStack spacing={2}>
                                <Badge colorScheme={getStatusColor(device.latest_status)} size="sm">
                                  {device.latest_status}
                                </Badge>
                                {device.is_blocked && <Badge colorScheme="red" size="sm">Blocked</Badge>}
                                {device.has_alarm && <Badge colorScheme="red" size="sm">Alarm</Badge>}
                                {device.battery && (
                                  <Badge colorScheme={getBatteryHealthColor(device.battery)} size="sm">
                                    {Math.round((device.battery / 6000) * 100)}%
                                  </Badge>
                                )}
                              </HStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>
                </Grid>
              </VStack>
            )}
          </TabPanel>

          {/* Problem Analysis */}
          <TabPanel>
            <VStack spacing={6}>
              <Card w="full">
                <CardHeader>
                  <Heading size="md">Devices Requiring Attention</Heading>
                  <Text fontSize="sm" color="gray.500">
                    Devices with the most issues in the last 24 hours (ranked by severity)
                  </Text>
                </CardHeader>
                <CardBody>
                  {problemPatterns.length > 0 ? (
                    <VStack spacing={3} align="stretch">
                      {problemPatterns.map((device, index) => (
                        <Box key={device.device_id} p={4} borderWidth={1} borderRadius="md" 
                             borderColor={index < 3 ? 'red.200' : 'gray.200'}>
                          <HStack justify="space-between">
                            <VStack align="start" spacing={1}>
                              <HStack>
                                <Text fontSize="md" fontWeight="medium">{device.device_name}</Text>
                                <Badge colorScheme="red">Score: {device.issueScore}</Badge>
                              </HStack>
                              <Text fontSize="xs" color="gray.500" fontFamily="mono">{device.device_id}</Text>
                              <Text fontSize="xs" color="gray.500">
                                Last seen: {formatTimestamp(device.lastSeen)}
                              </Text>
                            </VStack>
                            <VStack align="end" spacing={1}>
                              <HStack spacing={2}>
                                {device.alarmCount > 0 && (
                                  <Badge colorScheme="red" size="sm">
                                    {device.alarmCount} alarms
                                  </Badge>
                                )}
                              </HStack>
                              <HStack spacing={2}>
                                {device.lowBatteryCount > 0 && (
                                  <Badge colorScheme="orange" size="sm">
                                    {device.lowBatteryCount} low battery
                                  </Badge>
                                )}
                                {device.offlineCount > 0 && (
                                  <Badge colorScheme="gray" size="sm">
                                    {device.offlineCount} offline
                                  </Badge>
                                )}
                              </HStack>
                            </VStack>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Text color="gray.500" textAlign="center" py={8}>
                      No devices with significant issues found in the last 24 hours
                    </Text>
                  )}
                </CardBody>
              </Card>

              <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} w="full">
                <Card>
                  <CardHeader>
                    <Heading size="md">Issue Trends</Heading>
                    <Text fontSize="sm" color="gray.500">
                      Hourly count of different issue types
                    </Text>
                  </CardHeader>
                  <CardBody>
                    <Box h="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={multiDeviceTimeline}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="timestamp" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="alarmCount"
                            stackId="1"
                            stroke={chartColors.alarm}
                            fill={chartColors.alarm}
                            fillOpacity={0.6}
                            name="Devices with Alarms"
                          />
                          <Area
                            type="monotone"
                            dataKey="blockedCount"
                            stackId="1"
                            stroke={chartColors.blocked}
                            fill={chartColors.blocked}
                            fillOpacity={0.6}
                            name="Blocked Devices"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <Heading size="md">Health Scores</Heading>
                    <Text fontSize="sm" color="gray.500">
                      Overall health metrics over time
                    </Text>
                  </CardHeader>
                  <CardBody>
                    <Box h="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={multiDeviceTimeline}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="timestamp" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis domain={[0, 100]} />
                          <RechartsTooltip formatter={(value: any) => [`${value}%`, 'Health Score']} />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="sensorActiveRate"
                            stroke={chartColors.sensor}
                            strokeWidth={3}
                            dot={{ fill: chartColors.sensor, strokeWidth: 2, r: 4 }}
                            name="Sensor Active Rate (%)"
                          />
                          <Line
                            type="monotone"
                            dataKey="alarmRate"
                            stroke={chartColors.alarm}
                            strokeWidth={3}
                            dot={{ fill: chartColors.alarm, strokeWidth: 2, r: 4 }}
                            name="Alarm Rate (%)"
                          />
                          <Line
                            type="monotone"
                            dataKey="blockedRate"
                            stroke={chartColors.blocked}
                            strokeWidth={3}
                            dot={{ fill: chartColors.blocked, strokeWidth: 2, r: 4 }}
                            name="Blocked Rate (%)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>
              </Grid>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={displayedLogs}
        keyField="uid"
        isLoading={isLoading}
        totalItems={batteryFilter ? displayedLogs.length : totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
      />

      {/* Cleanup Modal */}
      <Modal isOpen={isCleanupOpen} onClose={onCleanupClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cleanup Device Logs</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              This action will permanently delete logs older than the selected date.
            </Alert>
            
            <FormControl>
              <FormLabel>Delete logs older than:</FormLabel>
              <Input
                type="date"
                value={cleanupDate}
                onChange={(e) => setCleanupDate(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCleanupClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleCleanup}
              isLoading={isCleanupLoading}
            >
              Delete Logs
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DeviceLogs;
